/**
 * Utility helpers for the Projects feature.
 */

import type { HealthStatus, MilestoneStatus, DocumentType } from "./types.js";

/** Format a dollar amount compactly, e.g. $1.4M, $340K */
export function formatCurrency(amount: number): string {
  if (Math.abs(amount) >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(amount) >= 1_000) {
    return `$${Math.round(amount / 1_000)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

/** Format a date string into a human-readable short form. */
export function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

/** Map health status to CSS modifier class. */
export function healthClass(health: HealthStatus): string {
  return `health-badge--${health}`;
}

/** Map health status to label text. */
export function healthLabel(health: HealthStatus): string {
  const labels: Record<HealthStatus, string> = {
    green: "On Track",
    yellow: "At Risk",
    red: "Delayed",
  };
  return labels[health] ?? health;
}

/** Map milestone status to CSS modifier. */
export function milestoneStatusClass(status: MilestoneStatus): string {
  const map: Record<MilestoneStatus, string> = {
    done: "timeline__dot--done",
    "in-progress": "timeline__dot--in-progress",
    "at-risk": "timeline__dot--at-risk",
    "not-started": "",
  };
  return map[status] ?? "";
}

/** Emoji icon per document type. */
export function docTypeIcon(type: DocumentType): string {
  const icons: Record<DocumentType, string> = {
    architectural: "📐",
    structural: "🏗️",
    civil: "🗺️",
    permit: "📋",
    survey: "📍",
    environmental: "🌿",
    invoice: "💳",
    quote: "💬",
    meeting: "📝",
    photo: "📷",
    other: "📄",
  };
  return icons[type] ?? "📄";
}

/** Safe parse: returns null if API call throws. */
export async function safeApiCall<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch {
    return null;
  }
}
